import { ComponentProps, HTMLAttributes, PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

export const PageHeader = ({ className, children }: PropsWithChildren<{ className?: string }>) => (
  <div className={cn(`border-solid bg-white p-8`, className)}>{children}</div>
)

export const PageBody = ({ className, children }: PropsWithChildren<{ className?: string }>) => (
  <div className={cn(`border-t border-t-gray-200 bg-gray-10 p-8`, className)}>{children}</div>
)

export const PageTabs = ({
  triggers,
  children,
  className,
  tabListContainerClassName,
  variant = `default`,
  ...tabsProps
}: ComponentProps<typeof Tabs> & {
  triggers: ComponentProps<typeof TabsTrigger>[]
  tabListContainerClassName?: string
  variant?: 'default' | 'transparent'
}) => {
  const { t } = useTranslation(`common`)

  return (
    <Tabs className={cn(``, className)} {...tabsProps}>
      <div className={cn(`w-full px-6 pt-3`, variant === `default` && `bg-white`, tabListContainerClassName)}>
        <TabsList className={cn(`h-[54px]`, variant === `default` && `bg-white`, variant === `transparent` && `bg-transparent`)}>
          {triggers.map(({ className: triggerClassName, key, ...triggerProps }) => {
            if (triggerProps.disabled) {
              return (
                <Tooltip key={key ?? triggerProps.id}>
                  <TooltipTrigger asChild>
                    <TabsTrigger
                      role='none'
                      className={cn(`h-[54px] rounded-none bg-white px-8`, triggerClassName)}
                      variant={`underline`}
                      {...triggerProps}
                    />
                  </TooltipTrigger>
                  <TooltipContent>{t(`layout.tabs.tooltip_locked`)}</TooltipContent>
                </Tooltip>
              )
            }

            return (
              <TabsTrigger
                key={key ?? triggerProps.id}
                className={cn(`h-[54px] rounded-none px-8`, triggerClassName)}
                variant={variant === `default` ? `underlineWhite` : `underlineTransparent`}
                {...triggerProps}
              />
            )
          })}
        </TabsList>
      </div>
      {children}
    </Tabs>
  )
}

export const PageTitle = ({ className, children, ...props }: HTMLAttributes<HTMLHeadingElement>) => (
  <h1 className={cn(`page-title`, className)} {...props}>
    {children}
  </h1>
)

export const PageSubtitle = ({ className, children, ...props }: HTMLAttributes<HTMLParagraphElement>) => (
  <p className={cn(`page-subtitle`, className)} {...props}>
    {children}
  </p>
)
